import { Component, OnDestroy, OnInit } from "@angular/core";
import {
  Observable,
  OperatorFunction,
  Subject,
  Subscription,
} from "rxjs/index";
import { ImageTreeNode, ImageFileSimplified } from "../types";
import { TemplateImagesService } from "../services/template-images.service";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { PreviewImageComponent } from "./preview-image.component";
import { map } from "rxjs/operators";

@Component({
  templateUrl: "browse-uploaded-images.component.html",
  selector: "browse-upload-images",
  styles: [
    `
      .border-top {
        border-top: 1px solid #e5e5e5;
      }
      .flex-container__centered {
        display: flex;
        justify-content: center;
        align-items: initial;
      }
      .flex-container__centered.col {
        flex-direction: column;
      }
      .item-grow {
        flex-grow: 2;
      }
      .border-none {
        border: none;
      }
      .no-padding-left {
        padding-left: 0;
      }
    `,
  ],
})
export class BrowseUploadedImagesComponent implements OnInit, OnDestroy {
  onClose: Subject<ImageFileSimplified>;
  dirstructure$: Observable<Array<ImageTreeNode>>;
  userId: string;
  selectedNode: ImageTreeNode;
  baseUrl: string;
  hideAllOtherUsers: boolean;
  hideCurrentUser: boolean;
  hideTemporaryFolder: boolean;
  hideBackupFolder: boolean;

  private _sub: Subscription;

  constructor(
    private templateImagesService: TemplateImagesService,
    private modalService: BsModalService,
    private modalRef: BsModalRef
  ) {
    this.userId = this.templateImagesService.getSelectedUserIdFromURL();
    this._sub = new Subscription();
    this.baseUrl = "";
    this.selectedNode = null;
    this.dirstructure$ = this.templateImagesService.getImagesTree("");
    this.onClose = new Subject<ImageFileSimplified>();
  }

  ngOnInit(): void {
    this.initializeCheckboxes();
    this.filterImageDirectory();
    const config: any = { ...this.modalService.config.initialState };
    if (config.imageBlob) {
      this.baseUrl = config.baseUrl;
    }
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
  }

  success(): void {
    this.onClose.next({
      name: this.selectedNode.label,
      url: this.baseUrl + this.getPathFromSelectedNode(),
      path: this.getPathFromSelectedNode(),
    });
    this.closeModal();
  }

  closeModal(): void {
    this.modalRef.hide();
  }

  loadNode({ node }: { node?: ImageTreeNode }): void {
    if (node) {
      this._sub.add(
        this.templateImagesService
          .getImagesTree(node.label)
          .subscribe((resp) => (node.children = resp))
      );
    }
  }

  selectNode({ node }: { node?: ImageTreeNode }): void {
    this.selectedNode = node;
  }

  openPreview(): void {
    if (this.selectedNode === null) {
      alert("Please select an image.");
      return;
    }

    this.modalService.show(PreviewImageComponent, {
      initialState: {
        imageUrl: this.baseUrl + this.getPathFromSelectedNode(),
      },
      backdrop: "static",
      class: "modal-lg",
      keyboard: false, // disable esc key
    });
  }

  onChangeAllUser(value: boolean): void {
    this.hideAllOtherUsers = value;
    this.filterImageDirectory();
  }
  onChangeCurrentUser(value: boolean): void {
    this.hideCurrentUser = value;
    this.filterImageDirectory();
  }
  onChangeBackup(value: boolean): void {
    this.hideBackupFolder = value;
    this.filterImageDirectory();
  }
  onChangeTemporary(value: boolean): void {
    this.hideTemporaryFolder = value;
    this.filterImageDirectory();
  }

  private getPathFromSelectedNode(): string {
    if (this.selectedNode.parent) {
      return `${this.selectedNode.parent.label}/${this.selectedNode.label}`;
    }
    return `${this.selectedNode.label}`;
  }

  private filterImageDirectory(): void {
    const mapOperators: Array<
      OperatorFunction<ImageTreeNode[], ImageTreeNode[]>
    > = [];

    if (this.hideAllOtherUsers) {
      mapOperators.push(this.applyFilterByKey("", this.userId));
    }
    if (this.hideTemporaryFolder) {
      mapOperators.push(this.applyFilterByKey("tmp"));
    }
    if (this.hideBackupFolder) {
      mapOperators.push(this.applyFilterByKey("old"));
    }
    if (this.hideCurrentUser) {
      mapOperators.push(this.applyFilterByKey(this.userId));
    }

    this.dirstructure$ = this.templateImagesService
      .getImagesTree("")
      .pipe.apply(this.templateImagesService.getImagesTree(""), mapOperators);
  }

  private applyFilterByKey(
    keyToExclude: string,
    keyToAdd?: string
  ): OperatorFunction<ImageTreeNode[], ImageTreeNode[]> {
    return map((res: Array<ImageTreeNode>) => {
      if (Boolean(keyToAdd)) {
        return res.filter(
          (node) => node.label !== keyToExclude && node.label === keyToAdd
        );
      }
      return res.filter((node) => node.label !== keyToExclude);
    });
  }
  private initializeCheckboxes(): void {
    this.hideAllOtherUsers = true;
    this.hideBackupFolder = true;
    this.hideTemporaryFolder = true;
    this.hideCurrentUser = false;
  }
}
